import { useEffect, useRef, useState } from "react";
import { Upload, Link2, FolderOpen, Star, Trash2, GripVertical, FileText, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fileUrl } from "@/lib/api";
import { SectionTitle, Field, Pill } from "@/components/Bits";

const input = "w-full rounded-sm border border-line bg-white px-3 py-2 text-sm outline-none focus:border-lime";

export const VISUAL_TYPES = ["Rendering", "Moodboard", "Floor Plan", "Elevation", "Material Palette",
  "Inspiration", "Before Photo", "Detail", "Other"];

export const visualSrc = (v) => (v?.url ? v.url : v?.file_id ? fileUrl(v.file_id) : null);
export const isPdf = (v) => (v?.content_type || "").includes("pdf") || (v?.filename || "").toLowerCase().endsWith(".pdf");

function VisualPreview({ v, className = "aspect-[4/3] w-full" }) {
  const src = visualSrc(v);
  if (isPdf(v)) {
    return (
      <div className={`flex flex-col items-center justify-center gap-2 bg-[#F1F0EC] ${className}`}>
        <FileText className="h-6 w-6 text-fog" strokeWidth={1.5} />
        <span className="px-3 text-center text-[11px] text-fog">{v.filename || "PDF document"}</span>
      </div>
    );
  }
  return src ? (
    <img src={src} alt={v.title || v.type} className={`object-cover ${className}`} />
  ) : (
    <div className={`bg-line ${className}`} />
  );
}

export default function PresentationVisuals({ presentationId, projectId, initial = [], pinterestBoard, onChange }) {
  const [visuals, setVisuals] = useState(initial);
  const [mode, setMode] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [uploadType, setUploadType] = useState("Rendering");
  const [lastAdded, setLastAdded] = useState([]);
  const [projectFiles, setProjectFiles] = useState([]);
  const [urlForm, setUrlForm] = useState({ url: "", type: "Rendering", title: "" });
  const [pinForm, setPinForm] = useState({ pin_url: "", type: "Inspiration" });
  const [board, setBoard] = useState(pinterestBoard || null);
  const [boardForm, setBoardForm] = useState({
    url: pinterestBoard?.url || "",
    title: pinterestBoard?.title || "Inspiration board",
    visible_to_client: pinterestBoard?.visible_to_client || false,
  });
  const [dragId, setDragId] = useState(null);
  const fileRef = useRef();

  useEffect(() => setVisuals(initial), [initial]);

  useEffect(() => {
    if (mode !== "files" || !projectId) return;
    api
      .get("/files", { params: { project_id: projectId } })
      .then((r) => setProjectFiles(r.data.filter((f) => (f.content_type || "").startsWith("image/") || (f.content_type || "").includes("pdf"))))
      .catch(() => setProjectFiles([]));
  }, [mode, projectId]);

  const apply = (data) => {
    setVisuals(data.visuals);
    onChange?.(data.visuals);
    if (data.added_ids) setLastAdded(data.added_ids);
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("visual_type", uploadType);
    try {
      const { data } = await api.post(`/presentations/${presentationId}/visuals/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      apply(data);
      toast.success(`${files.length} visual${files.length === 1 ? "" : "s"} uploaded and saved to project files`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addExisting = async (fileId, type) => {
    try {
      const { data } = await api.post(`/presentations/${presentationId}/visuals`, { file_id: fileId, type });
      apply(data);
      toast.success("Project file added — the original is referenced, not copied");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const addUrl = async (e) => {
    e?.preventDefault?.();
    if (!urlForm.url.trim()) return toast.error("Paste an image URL first");
    try {
      const { data } = await api.post(`/presentations/${presentationId}/visuals`, urlForm);
      apply(data);
      setUrlForm({ url: "", type: "Rendering", title: "" });
      toast.success("Image added");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const addPin = async () => {
    if (!pinForm.pin_url.trim()) return toast.error("Paste a Pinterest pin link first");
    setBusy(true);
    try {
      const { data } = await api.post(`/presentations/${presentationId}/visuals/pinterest`, pinForm);
      apply(data);
      setPinForm({ ...pinForm, pin_url: "" });
      toast.success(data.message);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveBoard = async () => {
    try {
      const { data } = await api.put(`/presentations/${presentationId}/pinterest-board`, boardForm);
      setBoard(data.pinterest_board);
      toast.success("Pinterest board saved");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const clearBoard = async () => {
    try {
      await api.put(`/presentations/${presentationId}/pinterest-board`, { url: "" });
      setBoard(null);
      setBoardForm({ url: "", title: "Inspiration board", visible_to_client: false });
      toast.success("Board removed");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const patch = async (id, body) => {    try {
      const { data } = await api.patch(`/presentations/${presentationId}/visuals/${id}`, body);
      apply(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    try {
      const { data } = await api.delete(`/presentations/${presentationId}/visuals/${id}`);
      apply(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const setCover = async (id) => {
    try {
      const { data } = await api.post(`/presentations/${presentationId}/visuals/${id}/cover`);
      apply(data);
      toast.success("Presentation cover updated");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const applyTypeToRecent = async (type) => {
    for (const id of lastAdded) {
      await api.patch(`/presentations/${presentationId}/visuals/${id}`, { type });
    }
    const { data } = await api.get(`/presentations/${presentationId}/visuals`);
    setVisuals(data);
    onChange?.(data);
    toast.success(`Classified ${lastAdded.length} visual(s) as ${type}`);
  };

  const drop = async (targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = visuals.map((v) => v.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setDragId(null);
    try {
      const { data } = await api.post(`/presentations/${presentationId}/visuals/reorder`, { ids });
      apply(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <section className="card-surface p-7" data-testid="presentation-visuals-panel">
      <SectionTitle>Presentation Visuals</SectionTitle>

      <div className="flex flex-wrap gap-2">
        {[
          ["upload", "Upload Images", Upload],
          ["files", "Choose From Project Files", FolderOpen],
          ["pinterest", "Add From Pinterest", Link2],
          ["board", "Link Pinterest Board", FolderOpen],
          ["url", "Paste Image URL", Link2],
        ].map(([value, label, Icon]) => (
          <button
            key={value}
            type="button"
            data-testid={`visual-source-${value}`}
            onClick={() => setMode(value)}
            className={`inline-flex items-center gap-2 rounded-sm border px-4 py-2 text-xs transition-colors ${
              mode === value ? "border-lime bg-lime text-ink" : "border-line bg-white text-stone hover:border-fog"
            }`}
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={1.5} /> {label}
          </button>
        ))}
      </div>

      {mode === "upload" && (
        <div className="mt-6 rounded-sm border border-dashed border-fog/60 bg-[#FAFAF7] p-7 text-center" data-testid="visual-upload-zone">
          <Upload className="mx-auto h-6 w-6 text-fog" strokeWidth={1.5} />
          <p className="mt-3 text-sm text-ink">Drop renderings, moodboards, floor plans or elevations here</p>
          <p className="mt-1 text-xs text-fog">JPG, PNG, WEBP or PDF · up to 25MB each · select as many as you like</p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <select data-testid="visual-upload-type" value={uploadType} onChange={(e) => setUploadType(e.target.value)} className="rounded-sm border border-line bg-white px-3 py-2 text-xs outline-none focus:border-lime">
              {VISUAL_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
              {busy ? "Uploading…" : "Choose files"}
              <input ref={fileRef} data-testid="visual-upload-input" type="file" multiple accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,application/pdf" className="hidden" onChange={upload} disabled={busy} />
            </label>
          </div>
          {lastAdded.length > 1 && (
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2 border-t border-line pt-5" data-testid="visual-bulk-classify">
              <span className="text-xs text-stone">Classify the {lastAdded.length} just uploaded as</span>
              {["Rendering", "Moodboard", "Floor Plan", "Elevation"].map((t) => (
                <button key={t} type="button" data-testid={`visual-bulk-type-${t.replace(/\s/g, "-")}`} onClick={() => applyTypeToRecent(t)} className="rounded-sm border border-line px-3 py-1.5 text-xs text-stone hover:border-lime">
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === "files" && (
        <div className="mt-6" data-testid="visual-project-files">
          {projectFiles.length === 0 ? (
            <p className="text-sm text-fog">No eligible project files yet — upload from your computer instead.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {projectFiles.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`visual-existing-file-${f.id}`}
                  onClick={() => addExisting(f.id, uploadType)}
                  className="card-surface overflow-hidden text-left transition-colors hover:border-lime"
                >
                  <VisualPreview v={{ file_id: f.id, content_type: f.content_type, filename: f.original_filename }} className="aspect-[4/3] w-full" />
                  <div className="p-3">
                    <div className="truncate text-xs text-ink">{f.original_filename}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-[0.12em] text-fog">{f.category}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === "pinterest" && (
        <div className="mt-6 space-y-4" data-testid="pinterest-pin-form">
          <div className="grid gap-5 sm:grid-cols-3">
            <Field label="Pinterest pin link" className="sm:col-span-2">
              <input
                data-testid="pinterest-pin-input"
                value={pinForm.pin_url}
                onChange={(e) => setPinForm({ ...pinForm, pin_url: e.target.value })}
                placeholder="https://www.pinterest.com/pin/…"
                className={input}
              />
            </Field>
            <Field label="Visual Type">
              <select data-testid="pinterest-pin-type" value={pinForm.type} onChange={(e) => setPinForm({ ...pinForm, type: e.target.value })} className={input}>
                {VISUAL_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
          </div>
          <button data-testid="pinterest-pin-submit" type="button" onClick={addPin} disabled={busy} className="rounded-sm bg-ink px-4 py-2.5 text-sm text-white hover:bg-stone disabled:opacity-50">
            {busy ? "Fetching…" : "Add pin"}
          </button>
          <p className="text-[11px] leading-relaxed text-fog">
            We only read what Pinterest makes public. If a preview isn’t shared, the pin is still saved
            as an inspiration reference with a link back to the original — and you can always upload the
            image yourself instead.
          </p>
        </div>
      )}

      {mode === "board" && (
        <div className="mt-6 space-y-4" data-testid="pinterest-board-form">
          <div className="grid gap-5 sm:grid-cols-3">
            <Field label="Pinterest board link" className="sm:col-span-2">
              <input
                data-testid="pinterest-board-input"
                value={boardForm.url}
                onChange={(e) => setBoardForm({ ...boardForm, url: e.target.value })}
                placeholder="https://www.pinterest.com/studio/living-room/"
                className={input}
              />
            </Field>
            <Field label="Label the client sees">
              <input data-testid="pinterest-board-title" value={boardForm.title} onChange={(e) => setBoardForm({ ...boardForm, title: e.target.value })} className={input} />
            </Field>
          </div>
          <label className="flex items-center gap-3 text-sm text-ink">
            <input
              data-testid="pinterest-board-visibility"
              type="checkbox"
              checked={boardForm.visible_to_client}
              onChange={(e) => setBoardForm({ ...boardForm, visible_to_client: e.target.checked })}
              className="h-4 w-4 accent-[#BEC221]"
            />
            Visible to client (otherwise internal only)
          </label>
          <div className="flex flex-wrap gap-3">
            <button data-testid="pinterest-board-save" type="button" onClick={saveBoard} className="rounded-sm bg-ink px-4 py-2.5 text-sm text-white hover:bg-stone">Save board</button>
            {board && (
              <button data-testid="pinterest-board-clear" type="button" onClick={clearBoard} className="rounded-sm border border-line px-4 py-2.5 text-sm text-stone hover:border-clay hover:text-clay">Remove board</button>
            )}
          </div>
          {board && (
            <div className="rounded-sm border border-line bg-[#FAFAF7] p-4 text-xs" data-testid="pinterest-board-current">
              <div className="text-ink">{board.title}</div>
              <div className="mt-1 truncate text-fog">{board.url}</div>
              <div className="mt-2">
                <Pill tone={board.visible_to_client ? "bg-lime text-ink" : "bg-line text-stone"}>
                  {board.visible_to_client ? "Visible to client" : "Internal only"}
                </Pill>
              </div>
            </div>
          )}
        </div>
      )}

      {mode === "url" && (        <div className="mt-6 grid gap-5 sm:grid-cols-3" data-testid="visual-url-form">
          <Field label="Image URL" className="sm:col-span-2">
            <input
              data-testid="visual-url-input"
              value={urlForm.url}
              onChange={(e) => setUrlForm({ ...urlForm, url: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addUrl();
                }
              }}
              placeholder="https://…"
              className={input}
            />
          </Field>
          <Field label="Visual Type">
            <select data-testid="visual-url-type" value={urlForm.type} onChange={(e) => setUrlForm({ ...urlForm, type: e.target.value })} className={input}>
              {VISUAL_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <div className="sm:col-span-3">
            <button data-testid="visual-url-submit" type="button" onClick={addUrl} className="rounded-sm bg-ink px-4 py-2.5 text-sm text-white hover:bg-stone">Add image</button>
          </div>
        </div>
      )}

      <div className="mt-9">
        <div className="overline mb-4">
          {visuals.length} visual{visuals.length === 1 ? "" : "s"} · drag to set the order your client sees
        </div>
        {visuals.length === 0 ? (
          <p className="text-sm text-fog" data-testid="visuals-empty">No visuals yet. Upload your renderings to begin.</p>
        ) : (
          <div className="space-y-4" data-testid="visuals-list">
            {visuals.map((v) => (
              <div
                key={v.id}
                draggable
                onDragStart={() => setDragId(v.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => drop(v.id)}
                data-testid={`visual-row-${v.id}`}
                className={`flex flex-col gap-4 rounded-sm border p-4 transition-colors sm:flex-row sm:items-start ${
                  v.is_cover ? "border-lime bg-[#FCFCF7]" : "border-line bg-white"
                } ${dragId === v.id ? "opacity-50" : ""}`}
              >
                <GripVertical className="mt-1 hidden h-4 w-4 shrink-0 cursor-grab text-fog sm:block" strokeWidth={1.5} />
                <VisualPreview v={v} className="h-24 w-32 shrink-0 rounded-sm" />
                <div className="grid flex-1 gap-3 sm:grid-cols-2">
                  <Field label="Visual Type">
                    <select data-testid={`visual-type-${v.id}`} value={v.type} onChange={(e) => patch(v.id, { type: e.target.value })} className={input}>
                      {VISUAL_TYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Title (optional)">
                    <input data-testid={`visual-title-${v.id}`} defaultValue={v.title || ""} onBlur={(e) => patch(v.id, { title: e.target.value })} placeholder="e.g. Fireplace view" className={input} />
                  </Field>
                  <Field label="Caption (optional)" className="sm:col-span-2">
                    <input data-testid={`visual-caption-${v.id}`} defaultValue={v.caption || ""} onBlur={(e) => patch(v.id, { caption: e.target.value })} className={input} />
                  </Field>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {v.is_cover && <Pill tone="bg-lime text-ink" testId={`visual-cover-badge-${v.id}`}>Cover</Pill>}
                  <button
                    type="button"
                    data-testid={`visual-visibility-${v.id}`}
                    onClick={() => patch(v.id, { visible_to_client: !v.visible_to_client })}
                    className={`inline-flex items-center gap-2 rounded-sm border px-3 py-1.5 text-xs transition-colors ${
                      v.visible_to_client ? "border-lime text-lime-dark" : "border-line text-stone hover:border-fog"
                    }`}
                  >
                    {v.visible_to_client ? <Eye className="h-3.5 w-3.5" strokeWidth={1.5} /> : <EyeOff className="h-3.5 w-3.5" strokeWidth={1.5} />}
                    {v.visible_to_client ? "Client can see" : "Hidden"}
                  </button>
                  <button type="button" data-testid={`visual-set-cover-${v.id}`} onClick={() => setCover(v.id)} className="rounded-sm border border-line p-1.5 text-stone hover:border-lime hover:text-lime-dark" aria-label="Set as presentation cover">
                    <Star className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                  <button type="button" data-testid={`visual-remove-${v.id}`} onClick={() => remove(v.id)} className="rounded-sm border border-line p-1.5 text-stone hover:border-clay hover:text-clay" aria-label="Remove visual">
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-5 text-[11px] leading-relaxed text-fog">
          Uploads are filed under the project automatically, so a rendering only ever needs uploading once.
          New visuals stay hidden until you release them to the client.
        </p>
      </div>
    </section>
  );
}
